// Scores a guest submission with reCAPTCHA Enterprise, which every board page
// and the widget already load for voting. Resolves to a token, or "" when the
// script is blocked, offline or slow: the server logs a missing token and
// decides by its own mode (GUEST_CAPTCHA env), so a slow network never hangs a
// real person's post. On a phone the script may still be loading when the
// visitor taps submit, so this waits for it rather than giving up at once.
export const GUEST_CAPTCHA_SITE_KEY = "6LdcSVssAAAAABZPe71VEzUDIQjv9qbrW-rIGeVY";
// The "I'm not a robot" checkbox, shown when the server turns down a low
// invisible score (response has challenge_required). Its own key: a score key
// cannot render a challenge.
export const GUEST_CHALLENGE_SITE_KEY = "6LcKs9stAAAAAJ3V6MIVNsq47kL-qqQhjrkoP4fF";

function scriptReady() {
  return typeof grecaptcha !== "undefined" && !!grecaptcha.enterprise;
}

export function guestCaptchaToken(action, timeoutMs = 8000) {
  return new Promise((resolve) => {
    let settled = false;
    const done = (token) => {
      if (settled) return;
      settled = true;
      resolve(token || "");
    };
    const deadline = Date.now() + timeoutMs;
    const timer = setTimeout(() => done(""), timeoutMs);

    const execute = () => {
      try {
        grecaptcha.enterprise.ready(() => {
          grecaptcha.enterprise
            .execute(GUEST_CAPTCHA_SITE_KEY, { action })
            .then((token) => { clearTimeout(timer); done(token); })
            .catch(() => { clearTimeout(timer); done(""); });
        });
      } catch (e) {
        clearTimeout(timer);
        done("");
      }
    };

    const waitForScript = () => {
      if (scriptReady()) return execute();
      if (Date.now() >= deadline) return done("");
      setTimeout(waitForScript, 250);
    };
    waitForScript();
  });
}

// Draws the checkbox into `container` and resolves with its token once the
// visitor solves it. A widget can only be drawn once into an element, so each
// call starts from a fresh child; the token is single use and expires after
// two minutes, so callers resubmit as soon as it arrives.
export function renderGuestChallenge(container, action) {
  return new Promise((resolve) => {
    if (!container) return;
    container.innerHTML = "";
    const wrapper = document.createElement("div");
    const target = document.createElement("div");
    wrapper.appendChild(target);
    container.appendChild(wrapper);

    // Google draws the box at a fixed 304x78 and it cannot resize. In a
    // narrower form (the board sidebar is 278px) it is scaled down to the
    // form's width; below 250px it would be too small to read, so the
    // compact box is used instead.
    // Refits when the form's width changes, e.g. when the box itself makes
    // the sidebar tall enough to grow a scrollbar.
    const width = container.clientWidth;
    const fit = () => {
      const w = container.clientWidth;
      const scale = w < 304 && w >= 250 ? w / 304 : 1;
      target.style.transform = scale < 1 ? `scale(${scale})` : "";
      target.style.transformOrigin = "0 0";
      wrapper.style.height = scale < 1 ? `${Math.ceil(78 * scale)}px` : "";
    };
    fit();
    if (width >= 250 && typeof ResizeObserver !== "undefined") {
      new ResizeObserver(fit).observe(container);
    }

    const draw = () => {
      grecaptcha.enterprise.ready(() => {
        grecaptcha.enterprise.render(target, {
          sitekey: GUEST_CHALLENGE_SITE_KEY,
          action,
          size: width < 250 ? "compact" : "normal",
          callback: resolve,
        });
      });
    };
    const waitForScript = () => (scriptReady() ? draw() : setTimeout(waitForScript, 250));
    waitForScript();
  });
}
